import type { PageBlock } from "@/utils/customPageStore";
import AdminEditField from "@/components/feature/AdminEditField";
import {
  SectionAdminBar,
  useBlockConfig,
  AdminCTAButton,
} from "./shared";

interface CTABannerBlockProps {
  block: PageBlock;
  isAdmin: boolean;
  isDark: boolean;
  headingFont: string;
  headingStyle: React.CSSProperties;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  onUpdate?: (b: PageBlock) => void;
}

export default function CTABannerBlock({
  block,
  isAdmin,
  isDark,
  headingFont,
  headingStyle,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onUpdate,
}: CTABannerBlockProps) {
  const { get, setConfig } = useBlockConfig(block, onUpdate);
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-600";

  const sectionEyebrow = get("sectionEyebrow", "");
  const sectionHeading = get("sectionHeading", "");
  const sectionSubtext = get("sectionSubtext", "");
  const heading = get("heading", "");
  const subtext = get("subtext", "");
  const primaryLabel = get("primaryLabel", "Apply Now");
  const primaryHref = get("primaryHref", "#application");
  const secondaryLabel = get("secondaryLabel", "");
  const secondaryHref = get("secondaryHref", "#contact");

  return (
    <div className={`mb-8 ${isAdmin ? "rounded-xl overflow-hidden border border-green-200" : ""}`}>
      {isAdmin && (
        <SectionAdminBar
          layout="cta-banner"
          isDark={isDark}
          onDelete={onDelete}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          isFirst={isFirst}
          isLast={isLast}
        />
      )}
      <div className="py-12 px-6 rounded-2xl" style={{ background: isDark ? "#1f2937" : "#fff" }}>
        {(sectionEyebrow || sectionHeading || sectionSubtext) && (
          <div className="text-center mb-8">
            {sectionEyebrow && (
              <AdminEditField value={sectionEyebrow} onSave={(v) => setConfig("sectionEyebrow", v)} inputClassName="text-center text-sm">
                <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{sectionEyebrow}</span>
              </AdminEditField>
            )}
            {sectionHeading && (
              <AdminEditField value={sectionHeading} onSave={(v) => setConfig("sectionHeading", v)} inputClassName="text-center text-3xl md:text-4xl font-extrabold">
                <h2 className={`text-3xl md:text-4xl font-extrabold mt-3 mb-3 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                  {sectionHeading}
                </h2>
              </AdminEditField>
            )}
            {sectionSubtext && (
              <AdminEditField value={sectionSubtext} onSave={(v) => setConfig("sectionSubtext", v)} multiline inputClassName="text-center">
                <p className={`text-base max-w-2xl mx-auto ${subColor}`}>{sectionSubtext}</p>
              </AdminEditField>
            )}
          </div>
        )}

        <div className="rounded-2xl p-10 text-center" style={{ background: "linear-gradient(135deg, #fce7f3 0%, #dcfce7 100%)" }}>
          {heading && (
            <AdminEditField value={heading} onSave={(v) => setConfig("heading", v)} inputClassName="text-center text-2xl font-extrabold">
              <h3 className={`text-2xl font-extrabold mb-3 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                {heading}
              </h3>
            </AdminEditField>
          )}
          {subtext && (
            <AdminEditField value={subtext} onSave={(v) => setConfig("subtext", v)} multiline inputClassName="text-center">
              <p className={`text-base mb-6 ${subColor}`}>{subtext}</p>
            </AdminEditField>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <div className="flex flex-col items-center gap-1.5">
              {isAdmin && onUpdate ? (
                <>
                  <AdminEditField
                    value={primaryLabel}
                    onSave={(v) => setConfig("primaryLabel", v)}
                    inputClassName="text-center font-bold"
                  >
                    <AdminCTAButton variant="primary">{primaryLabel}</AdminCTAButton>
                  </AdminEditField>
                  <AdminEditField
                    value={primaryHref}
                    onSave={(v) => setConfig("primaryHref", v)}
                    inputClassName="text-xs text-center text-gray-400"
                  >
                    <span className="text-xs text-gray-400">URL: {primaryHref || "#application"}</span>
                  </AdminEditField>
                </>
              ) : (
                <button
                  onClick={() => { const url = primaryHref || "#application"; if (url.startsWith("http")) { window.open(url, "_blank", "noopener noreferrer"); } else if (url.startsWith("#")) { const el = document.querySelector(url); if (el) { el.scrollIntoView({ behavior: "smooth" }); } else { window.location.href = `/${url}`; } } else { window.location.href = url; } }}
                  className="bg-pink-500 hover:bg-pink-600 text-white font-bold px-8 py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors"
                >
                  {primaryLabel}
                </button>
              )}
            </div>
            {secondaryLabel && (
              <div className="flex flex-col items-center gap-1.5">
                {isAdmin && onUpdate ? (
                  <>
                    <AdminEditField
                      value={secondaryLabel}
                      onSave={(v) => setConfig("secondaryLabel", v)}
                      inputClassName="text-center font-bold"
                    >
                      <AdminCTAButton variant="outline">{secondaryLabel}</AdminCTAButton>
                    </AdminEditField>
                    <AdminEditField
                      value={secondaryHref}
                      onSave={(v) => setConfig("secondaryHref", v)}
                      inputClassName="text-xs text-center text-gray-400"
                    >
                      <span className="text-xs text-gray-400">URL: {secondaryHref || "#contact"}</span>
                    </AdminEditField>
                  </>
                ) : (
                  <button
                    onClick={() => { const url = secondaryHref || "#contact"; if (url.startsWith("http")) { window.open(url, "_blank", "noopener noreferrer"); } else if (url.startsWith("#")) { const el = document.querySelector(url); if (el) { el.scrollIntoView({ behavior: "smooth" }); } else { window.location.href = `/${url}`; } } else { window.location.href = url; } }}
                    className={`border-2 border-pink-400 font-bold px-8 py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                      isDark ? "text-pink-300 hover:bg-pink-900/30" : "text-pink-600 hover:bg-pink-50"
                    }`}
                  >
                    {secondaryLabel}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}